import React from "react";
import { Link } from "react-router-dom";
import SimpleMap from "../../features/Map";
import { motion } from "framer-motion";
import whatsappLogo from "../../assets/img/whatsapp.svg";
import Logo from "../../assets/img/marroccoartfull.jpg";
import {
  FaMapMarkerAlt,
  FaInstagram,
  FaFacebookF,
  FaPhone,
} from "react-icons/fa";
import { MdEmail, MdAccessTime } from "react-icons/md";

export default function Footer() {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut",
      },
    },
  };

  const linkHoverVariants = {
    hover: {
      color: "#406580",
      x: 5,
      transition: { duration: 0.2 },
    },
  };

  return (
    <footer>
      {/* Map Section */}
      <motion.section
        className="map relative z-0"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <SimpleMap />

        {/* Map overlay with contact details */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          viewport={{ once: true }}
          className="absolute bottom-8 left-8 bg-white/95 backdrop-blur-sm p-5 rounded-lg shadow-lg border border-gray-200 max-w-md hidden md:block"
        >
          <h3 className="text-[#406580] font-bold text-lg mb-3 flex items-center">
            <FaMapMarkerAlt className="mr-2" /> Ci trovi qui
          </h3>
          <p className="text-gray-700">
            Via Luigi Einaudi, 10, 00047 Marino RM, Italia
          </p>
          <div className="mt-3 flex gap-4">
            <motion.a
              href="tel:+3423971162"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center text-sm text-gray-600 hover:text-[#406580] transition-colors"
            >
              <FaPhone className="mr-1" /> +34 2397 11 62
            </motion.a>
            <motion.a
              href="mailto:marroccoart@gmail.com"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center text-sm text-gray-600 hover:text-[#406580] transition-colors"
            >
              <MdEmail className="mr-1" /> marroccoart@gmail.com
            </motion.a>
          </div>
        </motion.div>
      </motion.section>

      {/* Main Footer */}
      <motion.section
        className="footer w-full bg-white text-gray-800 pt-12 pb-2"
        variants={containerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
      >
        <div className="mx-auto w-full max-w-[100rem] px-6 lg:px-8">
          <motion.div
            variants={itemVariants}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10"
          >
            {/* Company Info */}
            <div className="space-y-5">
              <motion.div
                variants={itemVariants}
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 400 }}
              >
                <Link to={"/"} className="inline-block">
                  <img
                    src={Logo}
                    alt="Marrocco ART Logo"
                    className="h-16 w-auto"
                  />
                </Link>
              </motion.div>

              <motion.p
                variants={itemVariants}
                className="text-gray-600 leading-relaxed"
              >
                Marrocco ART è simbolo di artigianato italiano d'eccellenza
                nella lavorazione del ferro battuto, unendo tradizione e
                innovazione da oltre 40 anni.
              </motion.p>

              <motion.div
                variants={itemVariants}
                className="flex space-x-4 pt-2"
              >
                <motion.a
                  href="https://www.instagram.com/marroccogroup/"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#406580] hover:text-white transition-colors"
                >
                  <FaInstagram />
                </motion.a>
                <motion.a
                  href="https://www.facebook.com/people/Marrocco-Group/61552063162756/?sk=reels_tab"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#406580] hover:text-white transition-colors"
                >
                  <FaFacebookF />
                </motion.a>
                <motion.a
                  href="tel:+3423971162"
                  whileHover={{ scale: 1.1, y: -2 }}
                  whileTap={{ scale: 0.9 }}
                  className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-[#406580] hover:text-white transition-colors"
                >
                  <FaPhone />
                </motion.a>
              </motion.div>
            </div>

            {/* Opening Hours */}
            <motion.div variants={itemVariants} className="lg:ml-10">
              <h2 className="text-gray-900 font-semibold text-lg mb-5 flex items-center">
                Orari di Apertura
              </h2>
              <motion.dl
                className="text-gray-600 space-y-3"
                variants={itemVariants}
              >
                <motion.div
                  className="flex justify-between items-center pb-3 border-b border-gray-100"
                  variants={itemVariants}
                >
                  <dt className="font-medium">Lunedì - Venerdì</dt>
                  <dd className="text-[#406580]">08:00 - 17:00</dd>
                </motion.div>
                <motion.div
                  className="flex justify-between items-center pb-3 border-b border-gray-100"
                  variants={itemVariants}
                >
                  <dt className="font-medium">Sabato</dt>
                  <dd className="text-[#406580]">08:00 - 13:00</dd>
                </motion.div>
                <motion.div
                  className="flex justify-between items-center"
                  variants={itemVariants}
                >
                  <dt className="font-medium">Domenica</dt>
                  <dd className="text-red-500">Chiuso</dd>
                </motion.div>
              </motion.dl>
            </motion.div>

            {/* Links */}
            <motion.div variants={itemVariants} className="space-y-3">
              <h2 className="text-gray-900 font-semibold text-lg mb-5">
                Link Utili
              </h2>
              <ul className="space-y-3">
                <motion.li variants={itemVariants}>
                  <motion.div whileHover="hover">
                    <Link
                      to="/store"
                      className="flex items-center text-gray-600 hover:text-[#406580] transition-colors"
                    >
                      <motion.span variants={linkHoverVariants}>
                        Online Shop
                      </motion.span>
                      <motion.span
                        variants={linkHoverVariants}
                        className="ml-1 opacity-0 group-hover:opacity-100"
                      >
                        ›
                      </motion.span>
                    </Link>
                  </motion.div>
                </motion.li>
                <motion.li variants={itemVariants}>
                  <motion.div whileHover="hover">
                    <Link
                      to="/chi-siamo"
                      className="flex items-center text-gray-600 hover:text-[#406580] transition-colors"
                    >
                      <motion.span variants={linkHoverVariants}>
                        Chi Siamo
                      </motion.span>
                      <motion.span
                        variants={linkHoverVariants}
                        className="ml-1 opacity-0 group-hover:opacity-100"
                      >
                        ›
                      </motion.span>
                    </Link>
                  </motion.div>
                </motion.li>
                <motion.li variants={itemVariants}>
                  <motion.div whileHover="hover">
                    <Link
                      to="/contatti"
                      className="flex items-center text-gray-600 hover:text-[#406580] transition-colors"
                    >
                      <motion.span variants={linkHoverVariants}>
                        Contattaci
                      </motion.span>
                      <motion.span
                        variants={linkHoverVariants}
                        className="ml-1 opacity-0 group-hover:opacity-100"
                      >
                        ›
                      </motion.span>
                    </Link>
                  </motion.div>
                </motion.li>
                <motion.li variants={itemVariants}>
                  <motion.div whileHover="hover">
                    <Link
                      to="/eventi"
                      className="flex items-center text-gray-600 hover:text-[#406580] transition-colors"
                    >
                      <motion.span variants={linkHoverVariants}>
                        Eventi
                      </motion.span>
                      <motion.span
                        variants={linkHoverVariants}
                        className="ml-1 opacity-0 group-hover:opacity-100"
                      >
                        ›
                      </motion.span>
                    </Link>
                  </motion.div>
                </motion.li>
              </ul>
            </motion.div>

            {/* Legal */}
            <motion.div variants={itemVariants} className="space-y-3">
              <h2 className="text-gray-900 font-semibold text-lg mb-5">
                Informative Legali
              </h2>
              <ul className="space-y-3">
                <motion.li variants={itemVariants}>
                  <motion.div whileHover="hover">
                    <Link
                      to="/privacy-policy"
                      className="flex items-center text-gray-600 hover:text-[#406580] transition-colors"
                    >
                      <motion.span variants={linkHoverVariants}>
                        Privacy Policy
                      </motion.span>
                      <motion.span
                        variants={linkHoverVariants}
                        className="ml-1 opacity-0 group-hover:opacity-100"
                      >
                        ›
                      </motion.span>
                    </Link>
                  </motion.div>
                </motion.li>
                <motion.li variants={itemVariants}>
                  <motion.div whileHover="hover">
                    <Link
                      to="/terms-of-service"
                      className="flex items-center text-gray-600 hover:text-[#406580] transition-colors"
                    >
                      <motion.span variants={linkHoverVariants}>
                        Termini e Condizioni
                      </motion.span>
                      <motion.span
                        variants={linkHoverVariants}
                        className="ml-1 opacity-0 group-hover:opacity-100"
                      >
                        ›
                      </motion.span>
                    </Link>
                  </motion.div>
                </motion.li>
                <motion.li variants={itemVariants}>
                  <motion.div whileHover="hover">
                    <Link
                      to="/cookies"
                      className="flex items-center text-gray-600 hover:text-[#406580] transition-colors"
                    >
                      <motion.span variants={linkHoverVariants}>
                        Cookie Policy
                      </motion.span>
                      <motion.span
                        variants={linkHoverVariants}
                        className="ml-1 opacity-0 group-hover:opacity-100"
                      >
                        ›
                      </motion.span>
                    </Link>
                  </motion.div>
                </motion.li>
              </ul>
            </motion.div>
          </motion.div>
        </div>
      </motion.section>

      {/* Divider */}
      <motion.section
        className="w-full h-0.5 bg-gray-100"
        initial={{ scaleX: 0 }}
        whileInView={{ scaleX: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      ></motion.section>

      {/* Copyright */}
      <motion.section
        className="w-full py-6 flex items-center justify-center text-center bg-white text-gray-500 text-sm"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.3 }}
        viewport={{ once: true }}
      >
        <div className="max-w-screen-xl mx-auto px-6 lg:px-8">
          &copy; {new Date().getFullYear()} Marrocco Group. P.IVA 14211871000.
          Tutti i diritti riservati.
        </div>
      </motion.section>

      {/* Contact Button */}
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 1, type: "spring", stiffness: 200, damping: 15 }}
      >
        <Link
          className="fixed bottom-7 right-5 bg-[#406580] hover:bg-[#305570] rounded-full px-5 py-3 flex items-center justify-center z-50 shadow-lg transition-colors"
          to={"tel:+3423971162"}
        >
          <motion.div
            animate={{ scale: [1, 1.1, 1] }}
            transition={{ repeat: Infinity, repeatDelay: 2, duration: 0.6 }}
          >
            <img src={whatsappLogo} alt="WhatsApp" className="mr-3 w-5 h-5" />
          </motion.div>
          <motion.span className="text-white text-[14px] font-bold tracking-wide">
            CONTATTACI
          </motion.span>
        </Link>
      </motion.div>
    </footer>
  );
}
